import React, { useState } from 'react';
import SettingCard from './SettingCard';
import { panelLabel } from '../../utils/panelLabels';
import { tempUnit } from '../../utils/temperatureUnit';
import { DENSITY_UNIT_OPTIONS } from './densityUnits';

// Outlet blue, straight from the panel's own stylesheet.
const SENSOR_COLOR = '#428bca';

const OutletSensorSetup = ({ getSettingValue, handleEdit }) => {
  const [activeTab, setActiveTab] = useState(0);
  
  const tabs = [
    {
      title: 'Calibration',
      settings: [
        { name: 'outlet_moisture_slope', label: 'Moisture Slope' },
        { name: 'outlet_moisture_offset', label: 'Moisture Offset' },
        { name: 'outlet_temperature_slope', label: 'Temperature Slope' },
        { name: 'outlet_temperature_offset', label: 'Temperature Offset' },
        // Set through calibration — display only
        { name: 'outlet_temperature_coefficient', label: 'Temperature Coefficient', readOnly: true },
        { name: 'outlet_density_coefficient', label: 'Density Coefficient', readOnly: true },
        { name: 'outlet_product_density', label: 'Product Density (kg/hL)', readOnly: true }
      ]
    },
    {
      title: 'Setup',
      settings: [
        { name: 'outlet_compensation_enabled', label: 'Compensation Enabled', type: 'boolean' },
        // Shared across all three sensors — the panel syncs the unit
        { name: 'density_unit', label: 'Density Unit', type: 'select', options: DENSITY_UNIT_OPTIONS },
        { name: 'outlet_moisture_empty_voltage', label: 'Outlet Moisture Empty Voltage' },
        { name: 'outlet_calibration_max_adjustment', label: 'Outlet Calibration Max Adjustment' }
      ]
    }
  ];
  
  const currentTab = tabs[activeTab];
  
  // Get current sensor values
  const moistureValue = getSettingValue('outlet_moisture').value;
  const temperatureValue = getSettingValue('outlet_temperature').value;
  
  return (
    <div className="h-full flex flex-col">
      {/* Tab Headers */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {tabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            style={{
              backgroundColor: activeTab === idx ? SENSOR_COLOR : '',
            }}
            className={`py-4 rounded-lg font-bold text-xl shadow-md transition-all ${
              activeTab === idx
                ? 'text-white'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            {tab.title}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-2 gap-4 mb-6">
          {currentTab.settings.map((setting) => {
            const settingData = getSettingValue(setting.name);
            return (
              <SettingCard
                key={setting.name}
                name={panelLabel(setting.name, setting.label)}
                value={settingData.value}
                min={settingData.minRange}
                max={settingData.maxRange}
                readOnly={setting.readOnly}
                type={setting.type}
                options={setting.options}
                onEdit={(_, value) => handleEdit(setting.name, value)}
              />
            );
          })}
        </div>
      </div>
      
      {/* Live readings, on the calibration tab only, as on the panel */}
      {activeTab === 0 && (
      <div className="mt-auto pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border-2 p-4 text-center" style={{ backgroundColor: SENSOR_COLOR, borderColor: SENSOR_COLOR }}>
            <p className="text-3xl font-bold mb-2 text-white">
              {moistureValue !== null ? parseFloat(moistureValue).toFixed(1) : '--'}
              <span className="text-lg ml-2 text-white opacity-90">%</span>
            </p>
            <p className="font-semibold text-white">{panelLabel('moisture', 'Moisture')}</p>
          </div>
          
          <div className="rounded-lg border-2 p-4 text-center" style={{ backgroundColor: SENSOR_COLOR, borderColor: SENSOR_COLOR }}>
            <p className="text-3xl font-bold mb-2 text-white">
              {temperatureValue !== null ? parseFloat(temperatureValue).toFixed(1) : '--'}
              <span className="text-lg ml-2 text-white opacity-90">{tempUnit(getSettingValue('temperature_unit').value)}</span>
            </p>
            <p className="font-semibold text-white">{panelLabel('temperature', 'Temperature')}</p>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default OutletSensorSetup;
